import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { verifyEmail } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

type VerificationStatus = 'verifying' | 'success' | 'error';

/**
 * EmailVerificationCallback - Handles the verification link click
 * Route: /verify/:token
 */
const EmailVerificationCallback: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshUser, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<VerificationStatus>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [hasAttempted, setHasAttempted] = useState(false);

  useEffect(() => {
    // Guard against double-execution in React StrictMode
    if (hasAttempted) return;
    
    const verify = async () => {
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid verification link');
        return;
      }

      setHasAttempted(true);

      try {
        await verifyEmail(token);
        setStatus('success');
        
        // If user is logged in, refresh their data to update emailVerified status
        if (isAuthenticated) {
          try {
            await refreshUser();
          } catch (e) {
            // User refresh failed, but verification succeeded - they can re-login
          }
        }
        
        // Redirect to login/dashboard after a short delay
        setTimeout(() => {
          navigate(isAuthenticated ? '/my-splits' : '/login');
        }, 3000);
        
      } catch (error) {
        setStatus('error');
        setErrorMessage(error instanceof Error ? error.message : 'Verification failed');
      }
    };

    verify();
  }, [token, isAuthenticated, refreshUser, navigate, hasAttempted]);

  return (
    <div className="min-h-screen bg-gradient-secondary flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-surface-secondary border border-border-subtle rounded-lg p-8 text-center">
        
        {/* Verifying State */}
        {status === 'verifying' && (
          <>
            <div className="mx-auto w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mb-6">
              <svg className="animate-spin h-8 w-8 text-accent" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-content mb-2">
              Verifying Your Email
            </h1>
            <p className="text-content-secondary">
              Please wait while we verify your email address...
            </p>
          </>
        )}

        {/* Success State */}
        {status === 'success' && (
          <>
            <div className="mx-auto w-16 h-16 bg-success-muted rounded-full flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-content mb-2">
              Email Verified! 🎉
            </h1>
            <p className="text-content-secondary mb-6">
              Your email has been successfully verified. You can now access all features of CoTune.
            </p>
            <p className="text-content-muted text-sm mb-6">
              Redirecting you {isAuthenticated ? 'to your dashboard' : 'to login'}...
            </p>
            <Link
              to={isAuthenticated ? '/my-splits' : '/login'}
              className="btn-primary inline-block"
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Login Now'}
            </Link>
          </>
        )}

        {/* Error State */}
        {status === 'error' && (
          <>
            <div className="mx-auto w-16 h-16 bg-error-muted rounded-full flex items-center justify-center mb-6">
              <svg className="w-8 h-8 text-state-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-content mb-2">
              Verification Failed
            </h1>
            <p className="text-state-error mb-6">
              {errorMessage}
            </p>
            <p className="text-content-muted text-sm mb-6">
              The verification link may have expired or is invalid. Please try requesting a new verification email.
            </p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="btn-primary block w-full text-center"
              >
                Go to Login
              </Link>
              <Link
                to="/register"
                className="btn-secondary block w-full text-center"
              >
                Create New Account
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationCallback;
